import { fmtDate } from "@/lib/api";

const d = (v) => (v ? fmtDate(v) : "—");
const today = () => new Date().toISOString().slice(0, 10);

export const SUIVI = {
  reports: {
    label: "Rapports de chantier", add: "Nouveau rapport", pdf: true,
    empty: () => ({ date: today(), titre: "", type: "Journalier", avancement_constate: 0, travaux_realises: "", difficultes: "", prochaines_etapes: "" }),
    fields: [
      { name: "titre", label: "Titre *", required: true, full: true },
      { name: "date", label: "Date", type: "date" },
      { name: "type", label: "Type", options: ["Journalier", "Hebdomadaire", "Mensuel", "Spécial"] },
      { name: "avancement_constate", label: "Avancement constaté (%)", type: "number" },
      { name: "travaux_realises", label: "Travaux réalisés", type: "textarea", full: true },
      { name: "difficultes", label: "Difficultés rencontrées", type: "textarea", full: true },
      { name: "prochaines_etapes", label: "Prochaines étapes", type: "textarea", full: true },
    ],
    title: (e) => `${e.numero} · ${e.titre}`, sub: (e) => `${d(e.date)} · ${e.type} · avancement ${e.avancement_constate || 0} % · par ${e.by}`,
    extra: (e) => e.travaux_realises,
  },
  journal: {
    label: "Journal de chantier", add: "Nouvelle entrée", photos: true,
    empty: () => ({ date: today(), meteo: "Ensoleillé", effectif: 0, travaux: "", observations: "" }),
    fields: [
      { name: "date", label: "Date", type: "date" },
      { name: "meteo", label: "Météo", options: ["Ensoleillé", "Nuageux", "Pluie", "Orage", "Vent fort"] },
      { name: "effectif", label: "Effectif présent", type: "number" },
      { name: "travaux", label: "Travaux réalisés *", type: "textarea", required: true, full: true },
      { name: "observations", label: "Observations", type: "textarea", full: true },
    ],
    title: (e) => `${d(e.date)} · ${e.meteo || ""} · ${e.effectif || 0} pers.`,
    sub: (e) => e.travaux, extra: (e) => e.observations,
  },
  incidents: {
    label: "Incidents", add: "Signaler un incident", photos: true,
    empty: () => ({ date: today(), titre: "", description: "", gravite: "Mineure", responsable: "", statut: "Ouvert", resolution: "" }),
    fields: [
      { name: "titre", label: "Incident *", required: true, full: true },
      { name: "date", label: "Date", type: "date" },
      { name: "gravite", label: "Gravité", options: ["Mineure", "Majeure", "Critique"] },
      { name: "responsable", label: "Responsable du traitement" },
      { name: "statut", label: "Statut", options: ["Ouvert", "En traitement", "Clôturé"] },
      { name: "description", label: "Description", type: "textarea", full: true },
      { name: "resolution", label: "Résolution", type: "textarea", full: true },
    ],
    title: (e) => `${e.numero} · ${e.titre}`, sub: (e) => `${d(e.date)} · ${e.gravite}${e.responsable ? ` · ${e.responsable}` : ""}`,
    extra: (e) => e.resolution ? `Résolution : ${e.resolution}` : e.description, badge: (e) => e.statut,
  },
  quality: {
    label: "Contrôles qualité", add: "Nouveau contrôle",
    empty: () => ({ date: today(), point: "", task_id: "", resultat: "Conforme", commentaire: "", controleur: "", create_reserve: true }),
    fields: [
      { name: "point", label: "Point contrôlé *", required: true, full: true },
      { name: "date", label: "Date", type: "date" },
      { name: "task_id", label: "Tâche liée", options: "tasks" },
      { name: "resultat", label: "Résultat", options: ["Conforme", "Non conforme"] },
      { name: "controleur", label: "Contrôleur" },
      { name: "commentaire", label: "Commentaire", type: "textarea", full: true },
      { name: "create_reserve", label: "Créer une réserve si non conforme", type: "checkbox", createOnly: true, full: true },
    ],
    title: (e) => e.point, sub: (e) => `${d(e.date)}${e.task_titre ? ` · ${e.task_titre}` : ""}${e.controleur ? ` · ${e.controleur}` : ""}`,
    extra: (e) => [e.commentaire, e.reserve_numero && `Réserve ${e.reserve_numero} créée`].filter(Boolean).join(" — "), badge: (e) => e.resultat,
  },
  meetings: {
    label: "Réunions de chantier", add: "Nouvelle réunion",
    empty: () => ({ date: today(), objet: "", participants: "", ordre_du_jour: "", decisions: "", actions: "" }),
    fields: [
      { name: "objet", label: "Objet *", required: true, full: true },
      { name: "date", label: "Date", type: "date" },
      { name: "participants", label: "Participants" },
      { name: "ordre_du_jour", label: "Ordre du jour", type: "textarea", full: true },
      { name: "decisions", label: "Décisions", type: "textarea", full: true },
      { name: "actions", label: "Actions à mener", type: "textarea", full: true },
    ],
    title: (e) => `${e.numero} · ${e.objet}`, sub: (e) => `${d(e.date)}${e.participants ? ` · ${e.participants}` : ""}`,
    extra: (e) => [e.decisions && `Décisions : ${e.decisions}`, e.actions && `Actions : ${e.actions}`].filter(Boolean).join(" — "),
  },
  reserves: {
    label: "Réserves", add: "Nouvelle réserve", photos: true, correctionPhotos: true,
    empty: () => ({ description: "", localisation: "", assignment_id: "", echeance: "", statut: "Ouverte", correction: "" }),
    fields: [
      { name: "description", label: "Réserve *", required: true, full: true },
      { name: "localisation", label: "Localisation" },
      { name: "assignment_id", label: "Responsable (artisan / ouvrier affecté)", options: "assignments" },
      { name: "echeance", label: "Échéance", type: "date" },
      { name: "statut", label: "Statut", options: ["Ouverte", "Corrigée", "Levée"] },
      { name: "correction", label: "Correction réalisée", type: "textarea", full: true },
    ],
    title: (e) => `${e.numero} · ${e.description}`,
    sub: (e) => `${e.localisation || "—"} · ${e.responsable_name || "sans responsable"} · échéance ${d(e.echeance)}`,
    extra: (e) => [e.correction && `Correction : ${e.correction}`, e.leve_par && `Levée par ${e.leve_par}`].filter(Boolean).join(" — "), badge: (e) => e.statut,
    late: (e) => e.statut !== "Levée" && e.echeance && e.echeance < today(),
  },
};
